import React, { useState, useEffect } from 'react';
import { TrendingUp, BarChart3, AlertTriangle, Settings } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../services/api';

const CATEGORY_LABELS = {
  integrity: 'Integrity', compassion: 'Compassion', assurance: 'Assurance',
  respect: 'Respect', innovation: 'Innovation'
};

const CHART_COLORS = ['#2D5B8C', '#A63A3A', '#2F6E45', '#A86A12', '#5E4B8B', '#2B7A86'];

function AnalyticsDashboard({ user }) {
  const [view, setView] = useState('trend');
  const [periods, setPeriods] = useState([]);

  const [trendGroupBy, setTrendGroupBy] = useState('kelas');
  const [trendPeriodIds, setTrendPeriodIds] = useState([]);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const [compareGroupBy, setCompareGroupBy] = useState('kelas');
  const [comparePeriodId, setComparePeriodId] = useState('');
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const [atRiskPeriodId, setAtRiskPeriodId] = useState('');
  const [atRiskData, setAtRiskData] = useState(null);
  const [atRiskLoading, setAtRiskLoading] = useState(false);
  const [thresholdInput, setThresholdInput] = useState('');

  useEffect(() => {
    (async () => {
      const periodsRes = await api.getAnalyticsPeriods();
      const list = periodsRes.data || [];
      setPeriods(list);
      const active = list.find(p => p.is_active);
      if (active) {
        setTrendPeriodIds([active.id]);
        setComparePeriodId(String(active.id));
        setAtRiskPeriodId(String(active.id));
      }
    })();
  }, []);

  const loadTrend = async () => {
    if (trendPeriodIds.length === 0) return;
    setTrendLoading(true);
    try {
      const res = await api.getAnalyticsTrend({ groupBy: trendGroupBy, periodIds: trendPeriodIds.join(',') });
      setTrendData(res.data);
    } finally {
      setTrendLoading(false);
    }
  };

  const loadCompare = async () => {
    if (!comparePeriodId) return;
    setCompareLoading(true);
    try {
      const res = await api.getAnalyticsCompare({ groupBy: compareGroupBy, periodId: comparePeriodId });
      setCompareData(res.data);
    } finally {
      setCompareLoading(false);
    }
  };

  const loadAtRisk = async () => {
    setAtRiskLoading(true);
    try {
      const res = await api.getAnalyticsAtRisk(atRiskPeriodId ? { periodId: atRiskPeriodId } : {});
      setAtRiskData(res.data);
      setThresholdInput(String(res.data.threshold));
    } finally {
      setAtRiskLoading(false);
    }
  };

  useEffect(() => { if (view === 'trend' && trendPeriodIds.length) loadTrend(); }, [view, trendPeriodIds]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === 'compare' && comparePeriodId) loadCompare(); }, [view, comparePeriodId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === 'atrisk') loadAtRisk(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveThreshold = async () => {
    const value = Number(thresholdInput);
    if (!Number.isFinite(value) || value < 0 || value > 100) return;
    await api.updateAnalyticsSettings({ at_risk_threshold: value });
    loadAtRisk();
  };

  const togglePeriod = (id) => {
    setTrendPeriodIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  return (
    <div className="analytics-dashboard">
      <div className="analytics-subtabs">
        <button className={`admin-tab ${view === 'trend' ? 'active' : ''}`} onClick={() => setView('trend')}>
          <TrendingUp size={16} /><span className="tab-label">Tren Nilai</span>
        </button>
        <button className={`admin-tab ${view === 'compare' ? 'active' : ''}`} onClick={() => setView('compare')}>
          <BarChart3 size={16} /><span className="tab-label">Perbandingan Kategori</span>
        </button>
        <button className={`admin-tab ${view === 'atrisk' ? 'active' : ''}`} onClick={() => setView('atrisk')}>
          <AlertTriangle size={16} /><span className="tab-label">Mahasiswa Berisiko</span>
        </button>
      </div>

      {view === 'trend' && (
        <div className="analytics-panel">
          <div className="analytics-filters">
            <select className="form-input" value={trendGroupBy} onChange={e => setTrendGroupBy(e.target.value)}>
              <option value="mahasiswa">Per Mahasiswa</option>
              <option value="kelas">Per Kelas</option>
              <option value="angkatan">Per Angkatan</option>
            </select>
            <div className="period-checkboxes">
              {periods.map(p => (
                <label key={p.id}>
                  <input type="checkbox" checked={trendPeriodIds.includes(p.id)} onChange={() => togglePeriod(p.id)} /> {p.name}
                </label>
              ))}
            </div>
            <button className="btn-primary" onClick={loadTrend}>Tampilkan</button>
          </div>
          {trendLoading && <p>Memuat...</p>}
          {!trendLoading && trendData && trendData.series.length === 0 && <p>Belum ada data untuk filter ini.</p>}
          {!trendLoading && trendData && trendData.series.length > 0 && (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trendData.periods.map((p, idx) => {
                const point = { name: p.name };
                trendData.series.forEach(s => { point[s.label] = s.points[idx].avgPercentage; });
                return point;
              })}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                {trendData.series.map((s, idx) => (
                  <Line key={s.id} type="monotone" dataKey={s.label} stroke={CHART_COLORS[idx % CHART_COLORS.length]} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {view === 'compare' && (
        <div className="analytics-panel">
          <div className="analytics-filters">
            <select className="form-input" value={compareGroupBy} onChange={e => setCompareGroupBy(e.target.value)}>
              <option value="kelas">Per Kelas</option>
              <option value="prodi">Per Program Studi</option>
            </select>
            <select className="form-input" value={comparePeriodId} onChange={e => setComparePeriodId(e.target.value)}>
              <option value="">Pilih Periode</option>
              {periods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button className="btn-primary" onClick={loadCompare}>Tampilkan</button>
          </div>
          {compareLoading && <p>Memuat...</p>}
          {!compareLoading && compareData && compareData.series.length === 0 && <p>Belum ada data untuk filter ini.</p>}
          {!compareLoading && compareData && compareData.series.length > 0 && (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={compareData.categories.map((cat, idx) => {
                const point = { name: CATEGORY_LABELS[cat] };
                compareData.series.forEach(s => { point[s.label] = s.values[idx]; });
                return point;
              })}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                {compareData.series.map((s, idx) => (
                  <Bar key={s.id} dataKey={s.label} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {view === 'atrisk' && (
        <div className="analytics-panel">
          <div className="analytics-filters">
            <select className="form-input" value={atRiskPeriodId} onChange={e => setAtRiskPeriodId(e.target.value)}>
              <option value="">Periode Aktif</option>
              {periods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button className="btn-primary" onClick={loadAtRisk}>Tampilkan</button>
            {user.role === 'admin' && atRiskData && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Settings size={14} /> Threshold:
                <input type="number" min="0" max="100" className="form-input" style={{ width: '4rem' }}
                  value={thresholdInput} onChange={e => setThresholdInput(e.target.value)} />
                <button className="btn-secondary" onClick={saveThreshold}>Simpan</button>
              </span>
            )}
          </div>
          {atRiskLoading && <p>Memuat...</p>}
          {!atRiskLoading && atRiskData && atRiskData.students.length === 0 && (
            <p>Tidak ada mahasiswa di bawah threshold {atRiskData.threshold}%.</p>
          )}
          {!atRiskLoading && atRiskData && atRiskData.students.length > 0 && (
            <table className="data-table">
              <thead>
                <tr><th>NIM</th><th>Nama</th><th>Kelas</th><th>Skor Rata-rata</th><th>Kategori Terlemah</th></tr>
              </thead>
              <tbody>
                {atRiskData.students.map(s => (
                  <tr key={s.studentId}>
                    <td>{s.nim}</td>
                    <td>{s.name}</td>
                    <td>{s.kelasNama || '-'}</td>
                    <td>{s.percentage}%</td>
                    <td>{CATEGORY_LABELS[s.weakestCategory] || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
